import Cookies from "js-cookie";
import { serverURL } from "./apiConfig";
import { getOnline } from "lib/networkState";
import { responseCache } from "shared/money/responseCache";

// -----------------------------------------------------------------------------
//   API Client Utilities
// -----------------------------------------------------------------------------

/** Thrown when a mutation is attempted while offline (UI should disable controls; this is a safety net). */
export class OfflineWriteBlockedError extends Error {
    override name = "OfflineWriteBlockedError";
    constructor() {
        super("OFFLINE_WRITE_BLOCKED");
    }
}

function assertOnlineForWrite(): void {
    if (!getOnline()) {
        throw new OfflineWriteBlockedError();
    }
}

function isLikelyNetworkFailure(err: unknown): boolean {
    if (err instanceof TypeError) return true;
    if (err instanceof Error) {
        return /Failed to fetch|Load failed|NetworkError|network error/i.test(err.message);
    }
    return false;
}

const getStoredToken = (): string | undefined => Cookies.get("TOKEN");

let sessionGeneration = 0;
const inFlightGets = new Map<string, Promise<unknown>>();
const readGenerations = new Map<string, number>();
const sessionListeners = new Set<() => void>();

/** Invalidates responses initiated under an earlier authenticated session. */
export function invalidateApiSession(): void {
    sessionGeneration += 1;
    inFlightGets.clear();
    readGenerations.clear();
    sessionListeners.forEach((listener) => listener());
}

export function subscribeApiSession(listener: () => void): () => void {
    sessionListeners.add(listener);
    return () => { sessionListeners.delete(listener); };
}

/** Prevents a read started before a successful write from replacing fresh data. */
export function invalidateApiReads(prefix: string): void {
    for (const [key, generation] of readGenerations) {
        if (key.startsWith(`${responseCache.getCurrentUserId()}|${prefix}`)) {
            readGenerations.set(key, generation + 1);
            inFlightGets.delete(key);
        }
    }
    responseCache.removeMatching(prefix);
}

function tokenFingerprint(token: string | undefined): string {
    if (!token) return "anonymous";
    let first = 2166136261;
    let second = 0x9e3779b9;
    for (let i = 0; i < token.length; i += 1) {
        const code = token.charCodeAt(i);
        first = Math.imul(first ^ code, 16777619);
        second = Math.imul(second ^ code, 2246822519);
    }
    return `${token.length}:${(first >>> 0).toString(36)}:${(second >>> 0).toString(36)}`;
}

/**
 * Standard headers for JSON API requests
 */
const getJsonHeaders = (token?: string): HeadersInit => {
    const authToken = token ?? getStoredToken();
    const headers: HeadersInit = {
        'Content-Type': 'application/json; charset=UTF-8'
    };
    if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
    }
    return headers;
};

/**
 * Headers for FormData requests (file uploads)
 */
const getFormDataHeaders = (token?: string): HeadersInit => {
    const authToken = token ?? getStoredToken();
    const headers: HeadersInit = {};
    if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
    }
    return headers;
};

/**
 * Generic GET request wrapper (cached per user for offline read-through)
 */
export async function apiGet<T>(endpoint: string, token?: string): Promise<T> {
    const userId = responseCache.getCurrentUserId();
    const effectiveToken = token ?? getStoredToken();
    const sessionAtStart = sessionGeneration;
    const key = `${userId}|${endpoint}|${tokenFingerprint(effectiveToken)}`;
    if (!readGenerations.has(key)) readGenerations.set(key, 0);
    const readGenerationAtStart = readGenerations.get(key) ?? 0;
    if (!getOnline()) {
        const cached = responseCache.get<T>(endpoint, userId);
        if (cached !== null) {
            return cached;
        }
        throw new Error(extractErrorMessage(null, `GET ${endpoint} unavailable offline (no cached copy)`, undefined));
    }

    let request = inFlightGets.get(key) as Promise<T> | undefined;
    if (!request) {
        request = (async () => {
            const response = await fetch(`${serverURL}${endpoint}`, {
                method: 'GET',
                headers: getJsonHeaders(effectiveToken)
            });
            const payload = await parseJsonPayload(response);
            if (!response.ok) {
                throw new Error(extractErrorMessage(payload, `GET ${endpoint} failed with status ${response.status}`, response));
            }
            if (sessionGeneration === sessionAtStart && (readGenerations.get(key) ?? 0) === readGenerationAtStart) {
                responseCache.set(endpoint, payload, userId);
            }
            return payload as T;
        })();
        inFlightGets.set(key, request);
    }
    try {
        return await request;
    } catch (err) {
        const cached = responseCache.get<T>(endpoint, userId);
        if (cached !== null && isLikelyNetworkFailure(err)) {
            return cached;
        }
        throw err;
    } finally {
        if (inFlightGets.get(key) === request) inFlightGets.delete(key);
        // Keep the generation until session reset. SWR consumers can run after
        // this `finally`, and clearing it here could make an older callback
        // appear current if a newer read finished first.
    }
}

/**
 * Stale-while-revalidate: returns the last cached GET payload synchronously (if any) and starts exactly one
 * network revalidation. Use `done` for loading/error handling tied to that request; `onFresh` runs on success.
 */
export function apiGetSWR<T>(
    endpoint: string,
    onFresh: (data: T) => void,
    token?: string,
    options: { isCurrent?: () => boolean } = {},
): { stale: T | null; done: Promise<T>; cancel: () => void } {
    const userId = responseCache.getCurrentUserId();
    const sessionAtStart = sessionGeneration;
    const effectiveToken = token ?? getStoredToken();
    const key = `${userId}|${endpoint}|${tokenFingerprint(effectiveToken)}`;
    if (!readGenerations.has(key)) readGenerations.set(key, 0);
    const readGenerationAtStart = readGenerations.get(key) ?? 0;
    const stale = responseCache.get<T>(endpoint, userId);
    let subscribed = true;
    const done = apiGet<T>(endpoint, token).then((data) => {
        const isReadCurrent = (readGenerations.get(key) ?? 0) === readGenerationAtStart;
        if (subscribed && sessionGeneration === sessionAtStart && isReadCurrent && (options.isCurrent?.() ?? true)) onFresh(data);
        return data;
    });
    return { stale, done, cancel: () => { subscribed = false; } };
}

/**
 * Generic POST request wrapper for JSON
 */
export async function apiPost<T>(
    endpoint: string, 
    data: unknown, 
    token?: string
): Promise<T> {
    assertOnlineForWrite();
    const response = await fetch(`${serverURL}${endpoint}`, {
        method: 'POST',
        headers: getJsonHeaders(token),
        body: JSON.stringify(data)
    });

    const payload = await parseJsonPayload(response);
    if (!response.ok) {
        throw new Error(extractErrorMessage(payload, `POST ${endpoint} failed with status ${response.status}`, response));
    }

    return payload as T;
}

/**
 * Generic POST request wrapper for FormData (file uploads)
 */
export async function apiPostFormData<T>(
    endpoint: string,
    formData: FormData,
    token?: string
): Promise<T> {
    assertOnlineForWrite();
    const response = await fetch(`${serverURL}${endpoint}`, {
        method: 'POST',
        headers: getFormDataHeaders(token),
        body: formData
    });

    const payload = await parseJsonPayload(response);
    if (!response.ok) {
        throw new Error(extractErrorMessage(payload, `POST ${endpoint} failed with status ${response.status}`, response));
    }

    return payload as T;
}

/**
 * Generic PATCH request wrapper for JSON
 */
export async function apiPatch<T>(
    endpoint: string,
    data: unknown,
    token?: string
): Promise<T> {
    assertOnlineForWrite();
    const response = await fetch(`${serverURL}${endpoint}`, {
        method: 'PATCH',
        headers: getJsonHeaders(token),
        body: JSON.stringify(data)
    });

    const payload = await parseJsonPayload(response);
    if (!response.ok) {
        throw new Error(extractErrorMessage(payload, `PATCH ${endpoint} failed with status ${response.status}`, response));
    }

    return payload as T;
}

/**
 * Generic PUT request wrapper for JSON
 */
export async function apiPut<T>(
    endpoint: string,
    data: unknown,
    token?: string
): Promise<T> {
    assertOnlineForWrite();
    const response = await fetch(`${serverURL}${endpoint}`, {
        method: 'PUT',
        headers: getJsonHeaders(token),
        body: JSON.stringify(data)
    });

    const payload = await parseJsonPayload(response);
    if (!response.ok) {
        throw new Error(extractErrorMessage(payload, `PUT ${endpoint} failed with status ${response.status}`, response));
    }

    return payload as T;
}

/**
 * Generic PUT request wrapper for FormData (file uploads)
 */
export async function apiPutFormData<T>(
    endpoint: string,
    formData: FormData,
    token?: string
): Promise<T> {
    assertOnlineForWrite();
    const response = await fetch(`${serverURL}${endpoint}`, {
        method: 'PUT',
        headers: getFormDataHeaders(token),
        body: formData
    });

    const payload = await parseJsonPayload(response);
    if (!response.ok) {
        throw new Error(extractErrorMessage(payload, `PUT ${endpoint} failed with status ${response.status}`, response));
    }

    return payload as T;
}

/**
 * Generic DELETE request wrapper
 */
export async function apiDelete<T>(
    endpoint: string,
    data?: unknown,
    token?: string
): Promise<T> {
    assertOnlineForWrite();
    const headers = getJsonHeaders(token);

    const response = await fetch(`${serverURL}${endpoint}`, {
        method: 'DELETE',
        headers,
        body: data ? JSON.stringify(data) : undefined
    });

    const payload = await parseJsonPayload(response);
    if (!response.ok) {
        throw new Error(extractErrorMessage(payload, `DELETE ${endpoint} failed with status ${response.status}`, response));
    }

    return payload as T;
}

async function parseJsonPayload(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) return {};
    try {
        return JSON.parse(text);
    } catch {
        return { message: text };
    }
}

function formatRetryAfter(seconds: number): string {
    const total = Math.max(1, Math.ceil(seconds));
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    if (mins <= 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
}

function extractErrorMessage(payload: unknown, fallback: string, response?: Response): string {
    const retryAfterHeader = response?.headers?.get("retry-after");
    const retryAfterSeconds = Number(retryAfterHeader);

    if (typeof payload === "string" && payload.trim()) {
        return payload;
    }
    if (payload && typeof payload === "object") {
        const maybeMessage = (payload as { message?: unknown; error?: unknown; retryAfterText?: unknown; retryAfterSeconds?: unknown });
        // Prefer server `message` (intended user-facing); `error` is often a technical/detail field.
        if (typeof maybeMessage.message === "string" && maybeMessage.message.trim()) {
            return maybeMessage.message;
        }
        if (typeof maybeMessage.error === "string" && maybeMessage.error.trim()) {
            return maybeMessage.error;
        }
        if (typeof maybeMessage.retryAfterText === "string" && maybeMessage.retryAfterText.trim()) {
            return `Too many attempts. Try again in ${maybeMessage.retryAfterText}.`;
        }
        if (typeof maybeMessage.retryAfterSeconds === "number" && Number.isFinite(maybeMessage.retryAfterSeconds)) {
            return `Too many attempts. Try again in ${formatRetryAfter(maybeMessage.retryAfterSeconds)}.`;
        }
    }
    if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
        return `Too many attempts. Try again in ${formatRetryAfter(retryAfterSeconds)}.`;
    }
    return fallback;
}
